/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import root from 'window-or-global';
import { BREAKPOINTS } from '../../constants/screen';
import { commonValidation } from '../../constants/validation';
import { countryCodes } from '@/constants/locale';
import type { Locale } from '@/types/i18n';
import environmentConfig from '@/config/environment';
import { getCountryFromLocale } from '../getCountryFromLocale';
import { isServer } from '@/utils/isServer';

export const isEmptyObject = (obj: unknown): boolean => {
  return !!obj && obj.constructor === Object && Object.entries(obj).length === 0;
};

// this also considers iPad as mobile
export const isMobile = (): boolean => {
  if (typeof global !== 'undefined' && typeof global.navigator !== 'undefined') {
    return navigator.userAgent.match(
      /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i,
    )
      ? true
      : false;
  }

  return false;
};

export const getSalesChannel = (): string => {
  let deviceType = '';

  if (root.innerWidth >= BREAKPOINTS.DESKTOP_MIN) {
    deviceType = commonValidation.AZRWEB;
  } else if (
    root.innerWidth >= BREAKPOINTS.TABLET_MIN &&
    root.innerWidth <= BREAKPOINTS.TABLET_MAX
  ) {
    deviceType = commonValidation.AZRTAB;
  } else if (root.innerWidth < BREAKPOINTS.MOBILE_MAX) {
    deviceType = commonValidation.AZRMOB;
  }

  return deviceType;
};

export const constructPartGroupImageUrl = (partGroupId?: string, locale?: Locale): string => {
  if (!partGroupId) {
    return '';
  }

  const country = getCountryFromLocale(locale || 'en-US');

  if (partGroupId.toLowerCase().startsWith('azpg')) {
    return `${environmentConfig.PART_CARD_IMG_URL}/part_group/${country}/${partGroupId}.jpg`;
  } else {
    return `${environmentConfig.PART_CARD_IMG_URL}/part_terminology/${country}/${partGroupId}.jpg`;
  }
};

export const uniqueId = (): string => Math.random().toString(36).substr(2, 16);

export const isSafari = (): boolean =>
  !isServer &&
  !navigator.userAgent.toLowerCase().includes('chrome/') &&
  !navigator.userAgent.toLowerCase().includes('chromium/');

export const getBaseSchemaUrl = (locale: Locale): string => {
  return locale === countryCodes.mx
    ? 'https://www.autozone.com.mx'
    : locale === countryCodes.ptBr
      ? 'https://www.autozone.com.br'
      : 'https://www.autozone.com';
};

export const getSafeNumber = (value?: number | string | null): number => {
  const parsedValue = parseFloat(String(value));
  return isNaN(parsedValue) ? 0.0 : parsedValue;
};
